import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { notierungenApi } from '../../api_notierungen'
import { useAktion, useLaden } from '../../hooks'
import Delta from '../../komponenten/Delta'
import Meldung from '../../komponenten/Meldung'
import Meldungen from '../../komponenten/Meldungen'
import { datum, eurT, liefermonat, zeitpunkt } from '../../format'
import { notierungRegelnPfad } from '../../pfade'
import { NOTIERUNG_LAUFQUELLE, NOTIERUNG_LAUFSTATUS, NOTIERUNGSTATUS, regelInWorten, ZUORDNUNGTEXT } from '../../texte'
import type { NotierungLauf, NotierungPosition, NotierungRegel } from '../../typen'

/**
 * Ein Lauf der Rohstoffnotierungen im Detail: woher er kam (Mail mit Weiterleitungstext, Dateien),
 * je Position, was im Dokument steht (Lieferant, Produkt, Qualität, Ladeort), welcher Spalte sie
 * zugeordnet ist und warum, und je Liefermonat der Preis neben dem, was schon da ist (bisher am
 * selben Tag, Vortag, Δ, Warnung). Die Zuordnung lässt sich je Position ändern und „als Regel speichern" -
 * mit Spalte oder als „künftig nicht übernehmen"; hat eine Regel gegriffen, steht sie mit Link dabei.
 *
 * Übernehmen schreibt die Notierungen (Werte desselben Tags werden ersetzt, die alten stehen im
 * Buchungsprotokoll), Zurücknehmen stellt den Stand davor her.
 */
export default function LaufSeite() {
  const { id } = useParams()
  const laufId = Number(id)
  const { daten, fehler: ladeFehler, setDaten: setLauf } = useLaden(() => notierungenApi.lauf(laufId), [laufId])
  const lauf = daten
  const { fehler, erfolg, arbeitet, setErfolg, ausfuehren } = useAktion()
  const [mailOffen, setMailOffen] = useState(false)
  // Die Regeln, die gegriffen haben, in Worten - geladen nur, wenn es welche gibt, und neu, sobald eine dazukommt.
  const regelIds = [...new Set((lauf?.positionen ?? []).map((p) => p.regel_id).filter((r): r is number => r !== null && r !== undefined))].sort()
  const { daten: regeln } = useLaden(() => notierungenApi.regeln(), [regelIds.join(',')], { wenn: regelIds.length > 0 })
  const regelJeId = new Map((regeln ?? []).map((r) => [r.id, r]))

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [laufId])

  const pruefen = lauf?.status === 'pruefen'

  function aendern(p: NotierungPosition, daten: { quelle_id: number | null; uebernehmen: boolean; merken?: boolean }, meldung?: string) {
    void ausfuehren(async () => setLauf(await notierungenApi.positionSetzen(laufId, p.index, daten)), meldung)
  }

  function uebernehmen() {
    if (!lauf) return
    const werte = werteZahl(lauf)
    void ausfuehren(async () => {
      setLauf(await notierungenApi.uebernehmen(laufId))
    }, `Übernommen: ${werte} Werte stehen jetzt in der Tabelle.`)
  }

  function verwerfen() {
    if (!window.confirm('Diesen Lauf verwerfen? Es wird nichts übernommen; der Lauf bleibt in der Liste.')) return
    void ausfuehren(async () => setLauf(await notierungenApi.verwerfen(laufId)), 'Verworfen - es wurde nichts übernommen.')
  }

  function zuruecknehmen() {
    if (!window.confirm('Die Übernahme zurücknehmen? Die Tabelle steht danach wieder wie vor diesem Lauf.')) return
    void ausfuehren(async () => {
      setLauf(await notierungenApi.zuruecknehmen(laufId))
      setErfolg('Zurückgenommen - die Werte von vorher sind wiederhergestellt.')
    })
  }

  return (
    <>
      <div className="leiste">
        <Link className="knopf" to="/notierungen/importe">
          Zurück zur Liste
        </Link>
        <h2>
          Lauf {laufId}
          {lauf ? ` · ${lauf.lieferant_text ?? lauf.dateiname ?? 'ohne Namen'}` : ''}
        </h2>
        {lauf && <span className={`status ${NOTIERUNG_LAUFSTATUS[lauf.status].klasse}`}>{NOTIERUNG_LAUFSTATUS[lauf.status].text}</span>}
        {lauf?.automatisch && <span className="hinweis">ohne Klick übernommen</span>}
      </div>
      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />
      {lauf && (
        <>
          <p className="hinweis nz-lauf-kopf">
            {NOTIERUNG_LAUFQUELLE[lauf.quelle] ?? lauf.quelle}, angelegt {zeitpunkt(lauf.angelegt_am)}
            {lauf.notiert_am ? ` · Notierung vom ${datum(lauf.notiert_am)}` : ' · Tag der Notierung unbekannt'}
            {lauf.entschieden_von ? ` · entschieden von ${lauf.entschieden_von}` : ''}
            {lauf.dateien.length > 0 ? ` · Dateien: ${lauf.dateien.join(', ')}` : ''}
          </p>

          {lauf.hinweise.length > 0 && (
            <Meldung art={pruefen ? 'warnung' : 'hinweis'} titel={pruefen ? 'Warum dieser Lauf geprüft werden muss' : 'Hinweise'}>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {lauf.hinweise.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </Meldung>
          )}

          {lauf.mail && (
            <div className="karte">
              <div className="leiste" style={{ marginBottom: 0 }}>
                <b>Mail von {lauf.mail.absender}</b>
                <span className="hinweis">{lauf.mail.betreff ?? 'ohne Betreff'}</span>
                {lauf.mail.text && (
                  <button className="leise" onClick={() => setMailOffen((o) => !o)}>
                    {mailOffen ? 'Text ausblenden' : 'Text mit Weiterleitung zeigen'}
                  </button>
                )}
              </div>
              {mailOffen && lauf.mail.text && <pre className="mail-entwurf">{lauf.mail.text}</pre>}
            </div>
          )}

          {lauf.positionen.length === 0 && (
            <p className="leer">In diesem Lauf wurde keine Notierung erkannt. Den Grund nennen die Hinweise oben.</p>
          )}
          {lauf.positionen.map((p) => (
            <Position key={p.index} lauf={lauf} position={p} regel={p.regel_id ? regelJeId.get(p.regel_id) : undefined} gesperrt={!pruefen || arbeitet} onAendern={aendern} />
          ))}

          <div className="leiste" style={{ marginTop: 14 }}>
            {pruefen && werteZahl(lauf) === 0 && (
              <>
                {/* Nichts zu übernehmen (etwa Cargill Palmöl ab Hamburg): dann ist Verwerfen der Weg, nicht ein
                    Übernehmen, das nichts schreibt und trotzdem „übernommen" heißt. */}
                <button className="haupt" disabled={arbeitet} onClick={verwerfen}>
                  Verwerfen
                </button>
                <span className="hinweis">Keine Position wird übernommen - eine Spalte zuordnen oder den Lauf verwerfen.</span>
              </>
            )}
            {pruefen && werteZahl(lauf) > 0 && (
              <>
                <button className="haupt" disabled={arbeitet} onClick={uebernehmen}>
                  Übernehmen
                </button>
                <button disabled={arbeitet} onClick={verwerfen}>
                  Verwerfen
                </button>
                <span className="hinweis">
                  {zusammenfassung(lauf)} Ein vorhandener Wert desselben Tags wird ersetzt; der alte bleibt im Protokoll.
                </span>
              </>
            )}
            {lauf.status === 'uebernommen' && (
              <>
                <button disabled={arbeitet} onClick={zuruecknehmen}>
                  Zurücknehmen
                </button>
                <span className="hinweis">Stellt die Tabelle wieder her, wie sie vor diesem Lauf war.</span>
              </>
            )}
          </div>
        </>
      )}
    </>
  )
}

function uebernommenePositionen(lauf: NotierungLauf) {
  return lauf.positionen.filter((p) => p.uebernehmen && p.quelle_id !== null)
}

/** Wie viele Werte „Übernehmen" schreiben würde. */
function werteZahl(lauf: NotierungLauf): number {
  return uebernommenePositionen(lauf).reduce((n, p) => n + p.perioden.length, 0)
}

/** „3 von 4 Positionen mit 33 Werten werden übernommen." - unter den Knöpfen. */
function zusammenfassung(lauf: NotierungLauf): string {
  return `${uebernommenePositionen(lauf).length} von ${lauf.positionen.length} Positionen mit ${werteZahl(lauf)} Werten werden übernommen.`
}

function Position({
  lauf,
  position: p,
  regel,
  gesperrt,
  onAendern,
}: {
  lauf: NotierungLauf
  position: NotierungPosition
  /** die Regel, die gegriffen hat (sobald geladen) */
  regel?: NotierungRegel
  gesperrt: boolean
  onAendern: (p: NotierungPosition, daten: { quelle_id: number | null; uebernehmen: boolean; merken?: boolean }, meldung?: string) => void
}) {
  const warnungen = p.perioden.filter((x) => x.warnung).length
  const aktiv = p.uebernehmen && p.quelle_id !== null
  const kopf = [p.qualitaet && `${p.qualitaet}-Ware`, p.ladeort && `Ladeort ${p.ladeort}`, p.lieferbedingung].filter(Boolean).join(' · ')
  // Offen ist anfangs, was übernommen wird oder warnt. Danach entscheidet der Nutzer: wer „übernehmen“ abwählt,
  // will meist gleich „als Regel speichern“ anklicken - die Karte darf ihm dabei nicht zuklappen.
  const [offen, setOffen] = useState(aktiv || warnungen > 0)
  const kuenftig = aktiv ? `künftig in die Spalte ${p.quelle ?? ''}` : 'künftig nicht übernehmen'
  return (
    <details className={`karte nz-position${aktiv ? '' : ' aus'}`} open={offen} onToggle={(e) => setOffen(e.currentTarget.open)}>
      <summary>
        <span className={`status ${aktiv ? 'gebucht' : 'verworfen'}`}>{aktiv ? 'wird übernommen' : 'nicht übernehmen'}</span>{' '}
        <b>{p.produkt ?? 'Produkt unbekannt'}</b>
        <span className="hinweis">
          {' '}
          - {p.lieferant ?? 'Lieferant unbekannt'}
          {kopf ? ` · ${kopf}` : ''}
          {p.quelle ? ` → Spalte ${p.quelle}` : ''}
          {` · ${p.perioden.length} Monate`}
          {warnungen > 0 ? ` · ${warnungen} Warnung${warnungen === 1 ? '' : 'en'}` : ''}
        </span>
      </summary>
      <div className="leiste felder nz-zuordnung">
        <label className="feld">
          <span className="hinweis">Spalte</span>
          <select
            value={p.quelle_id ?? ''}
            disabled={gesperrt}
            onChange={(e) => {
              const quelle = e.target.value ? Number(e.target.value) : null
              onAendern(p, { quelle_id: quelle, uebernehmen: quelle !== null })
            }}
          >
            <option value="">- keiner Spalte zuordnen -</option>
            {lauf.quellen_zur_auswahl.map((q) => (
              <option key={q.id} value={q.id}>
                {q.rohstoff} · {q.bezeichnung}
              </option>
            ))}
          </select>
        </label>
        <label className="haekchen">
          <input
            type="checkbox"
            checked={p.uebernehmen}
            disabled={gesperrt || p.quelle_id === null}
            onChange={(e) => onAendern(p, { quelle_id: p.quelle_id, uebernehmen: e.target.checked })}
          />{' '}
          übernehmen
        </label>
        {p.regel_id ? (
          // Gegriffen hat eine Regel - oder die Entscheidung von Hand wurde eben als Regel gespeichert.
          <span className="hinweis">
            {p.zuordnung === 'regel' ? 'nach Regel' : `${p.zuordnung ? `${ZUORDNUNGTEXT[p.zuordnung]}, ` : ''}als Regel gespeichert`}:{' '}
            <Link to={notierungRegelnPfad({ regel: p.regel_id })} title="Die Regel ansehen oder ändern">
              {regel ? regelInWorten(regel) : `Nr. ${p.regel_id}`}
            </Link>
          </span>
        ) : p.zuordnung === 'regel' ? (
          // Läufe von vor den Zuordnungsregeln wissen nicht, welche es war.
          <span className="hinweis">
            zugeordnet nach Regel · <Link to={notierungRegelnPfad()}>Regeln</Link>
          </span>
        ) : (
          p.zuordnung && <span className="hinweis">zugeordnet {ZUORDNUNGTEXT[p.zuordnung]}</span>
        )}
        {!p.regel_id && p.zuordnung !== 'regel' && p.produkt && (
          // Gilt in beide Richtungen: mit Spalte „künftig in diese Spalte", abgewählt „künftig nicht übernehmen".
          <label className="haekchen" title={`Eine Regel anlegen: „${p.produkt}“ von ${p.lieferant ?? 'diesem Lieferanten'} ${kuenftig}.`}>
            <input
              type="checkbox"
              checked={false}
              disabled={gesperrt}
              onChange={() =>
                onAendern(p, { quelle_id: p.quelle_id, uebernehmen: aktiv, merken: true }, `Als Regel gespeichert: „${p.produkt}“ ${kuenftig}.`)
              }
            />{' '}
            als Regel speichern <span className="hinweis">({kuenftig})</span>
          </label>
        )}
      </div>
      {p.grund && <p className="nz-grund">{p.grund}</p>}
      <div className="tabellenrahmen nz-perioden">
        <table>
          <thead>
            <tr>
              <th>Liefermonat</th>
              <th className="zahl">Preis €/t</th>
              <th className="zahl">bisher</th>
              <th className="zahl">Vortag</th>
              <th className="zahl">zu Vortag</th>
              <th>Warnung</th>
            </tr>
          </thead>
          <tbody>
            {p.perioden.map((x) => (
              <tr key={x.liefermonat} className={x.warnung ? 'nz-warnzeile' : undefined}>
                <td>{liefermonat(x.liefermonat)}</td>
                <td className="zahl">{x.status === 'preis' ? eurT(x.preis) : <span className="nz-ohne-preis">{NOTIERUNGSTATUS[x.status]}</span>}</td>
                <td className="zahl" title={x.bisher !== null ? 'An diesem Tag steht schon ein Wert - die Übernahme ersetzt ihn.' : undefined}>
                  {eurT(x.bisher)}
                </td>
                <td className="zahl">{eurT(x.vortag_preis)}</td>
                <td className="zahl">
                  <Delta wert={x.delta} />
                </td>
                <td>{x.warnung ? <span className="nz-warnung">Warnung: {x.warnung}</span> : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}
